import { lazy, Suspense } from "react";
import type { ComponentType, LazyExoticComponent } from "react";
import type { RouteObject } from "react-router-dom";
import Home from "../pages/home/page";
import { PageErrorBoundary } from "../components/base/ErrorBoundary";

// Keep only the homepage in the startup bundle. Every other page (especially
// the admin CMS and its PDF/chart/editor dependencies) is fetched on demand.
const NotFound = lazy(() => import("../pages/NotFound"));
const GCCGuide = lazy(() => import("../pages/gcc-guide/page"));
const ListingDetailPage = lazy(() => import("../pages/gcc-guide/listing/page"));
const TripPlannerPage = lazy(() => import("../pages/gcc-guide/trip-planner/page"));
const SharedTripPage = lazy(() => import("../pages/gcc-guide/shared-trip/page"));
const GCCAdminPage = lazy(() => import("../pages/admin/gcc-guide/page"));
const Advertising = lazy(() => import("../pages/advertising/page"));
const ContactPage = lazy(() => import("../pages/contact/page"));
const DynamicArticlePage = lazy(() => import("../pages/article/DynamicArticlePage"));
const CategoryPage = lazy(() => import("../pages/category/page"));
const AboutPage = lazy(() => import("../pages/about/page"));
const VideosPage = lazy(() => import("../pages/videos/page"));
const SearchPage = lazy(() => import("../pages/search/page"));
const AdminLogin = lazy(() => import("../pages/admin/login/page"));
const AdPackagesPage = lazy(() => import("../pages/admin/advertising/page"));
const AdPlacementsPage = lazy(() => import("../pages/admin/ad-placements/page"));
const AdRequestsPage = lazy(() => import("../pages/admin/ad-requests/page"));
const AboutAdminPage = lazy(() => import("../pages/admin/about/page"));
const ForgotPasswordPage = lazy(() => import("../pages/admin/forgot-password/page"));
const DashboardPage = lazy(() => import("../pages/admin/dashboard/page"));
const ArticlesManagementPage = lazy(() => import("../pages/admin/articles/page"));
const ArticleFormPage = lazy(() => import("../pages/admin/articles/ArticleFormPage"));
const UsersManagementPage = lazy(() => import("../pages/admin/users/page"));
const CategoriesPage = lazy(() => import("../pages/admin/categories/page"));
const SettingsPage = lazy(() => import("../pages/admin/settings/page"));
const NewspaperPdfPage = lazy(() => import("../pages/newspaper-pdf/page"));
const AdminNewspaperPdfPage = lazy(() => import("../pages/admin/newspaper-pdf/page"));
const AdminVideosPage = lazy(() => import("../pages/admin/videos/page"));
const AdminInfographicsPage = lazy(() => import("../pages/admin/infographics/page"));
const AdminOpinionPage = lazy(() => import("../pages/admin/opinion/page"));
const AnalyticsPage = lazy(() => import("../pages/admin/analytics/page"));
const SocialLinksPage = lazy(() => import("../pages/admin/social-links/page"));
const ContactMessagesPage = lazy(() => import("../pages/admin/contact-messages/page"));
const OgPreviewPage = lazy(() => import("../pages/admin/og-preview/page"));
const OpinionArticlePage = lazy(() => import("../pages/opinion/page"));
const OpinionListPage = lazy(() => import("../pages/opinion/list"));
const InfographicsPage = lazy(() => import("../pages/infographics/page"));
const ProtectedRoute = lazy(() => import("../pages/admin/components/ProtectedRoute"));

function RouteFallback() {
  return <div className="min-h-screen bg-white" aria-busy="true" aria-label="Loading page" />;
}

function deferred(Page: LazyExoticComponent<ComponentType>) {
  return (
    <PageErrorBoundary>
      <Suspense fallback={<RouteFallback />}>
        <Page />
      </Suspense>
    </PageErrorBoundary>
  );
}

const routes: RouteObject[] = [
  // ── Public site ──
  { path: "/", element: <PageErrorBoundary><Home /></PageErrorBoundary> },
  { path: "/gcc-guide", element: deferred(GCCGuide) },
  { path: "/gcc-guide/listing/:id", element: deferred(ListingDetailPage) },
  { path: "/gcc-guide/trip-planner", element: deferred(TripPlannerPage) },
  { path: "/gcc-guide/trip/:id", element: deferred(SharedTripPage) },
  { path: "/advertising", element: deferred(Advertising) },
  { path: "/contact", element: deferred(ContactPage) },
  { path: "/article/:slug", element: deferred(DynamicArticlePage) },
  { path: "/category/:slug", element: deferred(CategoryPage) },
  { path: "/about", element: deferred(AboutPage) },
  { path: "/videos", element: deferred(VideosPage) },
  { path: "/search", element: deferred(SearchPage) },
  { path: "/newspaper-pdf", element: deferred(NewspaperPdfPage) },
  { path: "/infographics", element: deferred(InfographicsPage) },
  { path: "/opinion", element: deferred(OpinionListPage) },
  { path: "/opinion/:id", element: deferred(OpinionArticlePage) },

  // ── Admin ──
  { path: "/admin", element: deferred(AdminLogin) },
  { path: "/admin/forgot-password", element: deferred(ForgotPasswordPage) },

  // ── متاح للجميع (admin + writer) ──
  {
    path: "/admin/dashboard",
    element: (
      <PageErrorBoundary>
        <ProtectedRoute allowedRoles={["admin", "writer"]}>
          <DashboardPage />
        </ProtectedRoute>
      </PageErrorBoundary>
    ),
  },
  {
    path: "/admin/articles",
    element: (
      <PageErrorBoundary>
        <ProtectedRoute allowedRoles={["admin", "writer"]}>
          <ArticlesManagementPage />
        </ProtectedRoute>
      </PageErrorBoundary>
    ),
  },
  {
    path: "/admin/articles/new",
    element: (
      <PageErrorBoundary>
        <ProtectedRoute allowedRoles={["admin", "writer"]}>
          <ArticleFormPage />
        </ProtectedRoute>
      </PageErrorBoundary>
    ),
  },
  {
    path: "/admin/articles/edit/:slug",
    element: (
      <PageErrorBoundary>
        <ProtectedRoute allowedRoles={["admin", "writer"]}>
          <ArticleFormPage />
        </ProtectedRoute>
      </PageErrorBoundary>
    ),
  },
  {
    path: "/admin/videos",
    element: (
      <PageErrorBoundary>
        <ProtectedRoute allowedRoles={["admin", "writer"]}>
          <AdminVideosPage />
        </ProtectedRoute>
      </PageErrorBoundary>
    ),
  },

  // ── مخصصة للمسؤولين فقط (admin only) ──
  {
    path: "/admin/users",
    element: (
      <PageErrorBoundary>
        <ProtectedRoute allowedRoles={["admin"]}>
          <UsersManagementPage />
        </ProtectedRoute>
      </PageErrorBoundary>
    ),
  },
  {
    path: "/admin/gcc-guide",
    element: (
      <PageErrorBoundary>
        <ProtectedRoute allowedRoles={["admin"]}>
          <GCCAdminPage />
        </ProtectedRoute>
      </PageErrorBoundary>
    ),
  },
  {
    path: "/admin/categories",
    element: (
      <PageErrorBoundary>
        <ProtectedRoute allowedRoles={["admin"]}>
          <CategoriesPage />
        </ProtectedRoute>
      </PageErrorBoundary>
    ),
  },
  {
    path: "/admin/settings",
    element: (
      <PageErrorBoundary>
        <ProtectedRoute allowedRoles={["admin"]}>
          <SettingsPage />
        </ProtectedRoute>
      </PageErrorBoundary>
    ),
  },
  {
    path: "/admin/advertising",
    element: (
      <PageErrorBoundary>
        <ProtectedRoute allowedRoles={["admin"]}>
          <AdPackagesPage />
        </ProtectedRoute>
      </PageErrorBoundary>
    ),
  },
  {
    path: "/admin/about",
    element: (
      <PageErrorBoundary>
        <ProtectedRoute allowedRoles={["admin"]}>
          <AboutAdminPage />
        </ProtectedRoute>
      </PageErrorBoundary>
    ),
  },
  {
    path: "/admin/ad-placements",
    element: (
      <PageErrorBoundary>
        <ProtectedRoute allowedRoles={["admin"]}>
          <AdPlacementsPage />
        </ProtectedRoute>
      </PageErrorBoundary>
    ),
  },
  {
    path: "/admin/ad-requests",
    element: (
      <PageErrorBoundary>
        <ProtectedRoute allowedRoles={["admin"]}>
          <AdRequestsPage />
        </ProtectedRoute>
      </PageErrorBoundary>
    ),
  },
  {
    path: "/admin/newspaper-pdf",
    element: (
      <PageErrorBoundary>
        <ProtectedRoute allowedRoles={["admin", "writer"]}>
          <AdminNewspaperPdfPage />
        </ProtectedRoute>
      </PageErrorBoundary>
    ),
  },
  {
    path: "/admin/infographics",
    element: (
      <PageErrorBoundary>
        <ProtectedRoute allowedRoles={["admin"]}>
          <AdminInfographicsPage />
        </ProtectedRoute>
      </PageErrorBoundary>
    ),
  },

  {
    path: "/admin/opinion",
    element: (
      <PageErrorBoundary>
        <ProtectedRoute allowedRoles={["admin"]}>
          <AdminOpinionPage />
        </ProtectedRoute>
      </PageErrorBoundary>
    ),
  },
  {
    path: "/admin/analytics",
    element: (
      <PageErrorBoundary>
        <ProtectedRoute allowedRoles={["admin"]}>
          <AnalyticsPage />
        </ProtectedRoute>
      </PageErrorBoundary>
    ),
  },
  {
    path: "/admin/social-links",
    element: (
      <PageErrorBoundary>
        <ProtectedRoute allowedRoles={["admin"]}>
          <SocialLinksPage />
        </ProtectedRoute>
      </PageErrorBoundary>
    ),
  },
  {
    path: "/admin/contact-messages",
    element: (
      <PageErrorBoundary>
        <ProtectedRoute allowedRoles={["admin"]}>
          <ContactMessagesPage />
        </ProtectedRoute>
      </PageErrorBoundary>
    ),
  },

  {
    path: "/admin/og-preview",
    element: (
      <PageErrorBoundary>
        <ProtectedRoute allowedRoles={["admin"]}>
          <OgPreviewPage />
        </ProtectedRoute>
      </PageErrorBoundary>
    ),
  },

  { path: "*", element: deferred(NotFound) },
];

export default routes;
